import { Avatar, Box, Button, CircularProgress, Dialog, Divider, IconButton, SxProps, Typography, useMediaQuery } from "@mui/material"
import { pxToRem, pxTovW } from "libs/shared-ui/src/commonUtils/resizeUtils";
import { IStyles } from "libs/shared-ui/src/commonUtils/styleUtils";
import { theme } from "libs/shared-ui/src/theme/themeProvider";
import ImageWrapper from "../../elements/ImageWrapper";
import { IconWrapper } from "../../elements/IconWrapper/Index";
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { AssessmentTask } from "@protos/learning_management/lms.assessment.common.apis_pb";
import { ProjectGroupMarkSummary } from "@protos/learning_management/lms.assessment.teacher.apis_pb";
import { isProjectDeadlinePassed } from "libs/shared-ui/src/commonUtils/utilFunctions";
import { Timestamp } from "@bufbuild/protobuf";
// Uncomment when the project icon is added to the bucket.
// import { getMediaBasePath } from "libs/shared-ui/src/commonUtils/images";
import { Fragment, MouseEvent, useState } from "react";

const GROUP_DETAILS_GREEN = '#0AA34F';
const GROUP_DETAILS_NAVY = '#475D99';

const getGroupMemberInitials = (name: string) =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');

const styles: IStyles = {
    root: {
        height: { xs: pxToRem(158), md: pxTovW(290), lg: pxTovW(210) },
        width: { xs: pxToRem(340), md: pxTovW(590), lg: pxTovW(433) },
        borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
        backgroundColor: '#FFFFFF',
        border: '1px solid #E0DFDE',
        padding: {
            xs: pxToRem(8),
            md: `${pxTovW(18)} ${pxTovW(15)}`,
            lg: `${pxTovW(13)} ${pxTovW(12)}`,
        },
        display: 'flex',
        flexDirection: 'column',
        // alignItems: 'stretch',
        gap: { xs: pxToRem(4), md: pxTovW(2), lg: pxTovW(3) },
        boxSizing: 'border-box',
        boxShadow: `0px 0px ${pxToRem(11)} #E7E7E7D9`,
        marginX: 'auto',
        cursor: 'pointer',
    },
    imageBox: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        height: { xs: '100%', md: '100%', lg: pxTovW(121) },
        width: { xs: pxToRem(103), md: pxTovW(221), lg: pxTovW(110) },
        minWidth: { xs: pxToRem(103), md: pxTovW(221), lg: pxTovW(111) },
        boxSizing: 'border-box',
        borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
        backgroundColor: '#E0DFDE',
        position: 'relative',
    },
    image: {
        width: '100%',
        objectFit: 'cover',
        borderRadius: { xs: pxToRem(16), md: pxTovW(16) },
    },
    iconBox: {
        display: 'flex',
        gap: { xs: pxToRem(6), md: pxTovW(8) },
        alignItems: 'center',
        width: 'max-content',
    },

    BadgeContainer: {
        boxSizing: 'border-box',
        position: 'absolute',
        width: '100%',
        height: { xs: pxToRem(39), md: pxTovW(53) },
        top: { xs: pxToRem(16), md: pxTovW(20) },
    },

    popoverBox: {
        '& .MuiPopover-root': {},
        '& .MuiPopover-paper': {
            // width: { xs: pxTovW(277), md: pxTovW(183), lg: pxTovW(183) },
            borderRadius: { xs: pxToRem(10), md: pxToRem(10) },
        },
    },

    // ----- Group details popup -----
    gdPaper: {
        borderRadius: { xs: pxToRem(14), md: pxTovW(20) },
        width: { xs: pxToRem(330), md: pxTovW(560) },
        maxWidth: '92vw',
        backgroundColor: '#FFFFFF',
        boxShadow: `0px ${pxToRem(10)} ${pxToRem(40)} rgba(0, 0, 0, 0.18)`,
        overflow: 'hidden',
    },
    gdHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: pxToRem(10), md: pxTovW(12) },
        padding: {
            xs: `${pxToRem(14)} ${pxToRem(16)}`,
            md: `${pxTovW(16)} ${pxTovW(24)}`,
        },
        borderBottom: '1px solid #E8E8E8',
    },
    gdTitle: {
        fontWeight: 700,
        color: '#1D1D1D',
        fontSize: { xs: pxToRem(16), md: pxTovW(20) },
    },
    gdClose: {
        color: '#6B6B6B',
        padding: { xs: pxToRem(4), md: pxTovW(4) },
    },
    gdContent: {
        display: 'flex',
        flexDirection: 'column',
        padding: {
            xs: `${pxToRem(16)} ${pxToRem(16)}`,
            md: `${pxTovW(20)} ${pxTovW(24)}`,
        },
        gap: { xs: pxToRem(12), md: pxTovW(14) },
        maxHeight: { xs: '60vh', md: '62vh' },
        overflowY: 'auto',
    },
    gdRow: {
        display: 'flex',
        alignItems: 'center',
        gap: { xs: pxToRem(12), md: pxTovW(14) },
    },
    gdIcon: {
        flexShrink: 0,
        color: GROUP_DETAILS_NAVY,
        fontSize: { xs: pxToRem(24), md: pxTovW(26) },
    },
    gdPersonIcon: {
        flexShrink: 0,
        color: GROUP_DETAILS_GREEN,
        fontSize: { xs: pxToRem(24), md: pxTovW(26) },
    },
    gdLabel: {
        color: '#1D1D1D',
        fontWeight: 700,
        lineHeight: 1.3,
        fontSize: { xs: pxToRem(15), md: pxTovW(16) },
    },
    gdValue: {
        color: '#6B6B6B',
        fontWeight: 400,
        lineHeight: 1.3,
        fontSize: { xs: pxToRem(13), md: pxTovW(14) },
    },
    gdMembersRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: pxToRem(12), md: pxTovW(14) },
    },
    gdMembersList: {
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: pxToRem(10), md: pxTovW(12) },
        mt: { xs: pxToRem(8), md: pxTovW(10) },
    },
    gdMemberItem: {
        display: 'flex',
        alignItems: 'center',
        gap: { xs: pxToRem(10), md: pxTovW(12) },
    },
    gdMemberAvatar: {
        height: { xs: pxToRem(26), md: pxTovW(28) },
        width: { xs: pxToRem(26), md: pxTovW(28) },
        fontSize: { xs: pxToRem(11), md: pxTovW(12) },
        fontWeight: 600,
        bgcolor: GROUP_DETAILS_NAVY,
    },
    gdMemberName: {
        color: '#2C2C2C',
        fontWeight: 400,
        fontSize: { xs: pxToRem(14), md: pxTovW(15) },
    },
    gdDivider: {
        borderColor: '#E8E8E8',
    },
    gdGroupDivider: {
        borderColor: '#D0D0D0',
        borderBottomWidth: '2px',
    },
    gdLoader: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: { xs: pxToRem(24), md: pxTovW(28) },
    },
    gdMemberRoll: {
        color: '#9A9A9A',
        fontWeight: 400,
    },
};
interface IProps {
    project?: AssessmentTask;
    onClick?: () => void;
    rootStyles?: SxProps;
    /** Name shown against "Created By"; falls back to the project's teacher name. */
    createdBy?: string;
    /**
     * Real collaboration groups of the project with their members. One entry per
     * group - when a project has two groups the popup shows both, each with its
     * own members. Left undefined until loaded (see `onDetailsOpen`).
     */
    groupDetails?: ProjectGroupMarkSummary[];
    /** Show a loader inside the popup while `groupDetails` is being fetched. */
    groupDetailsLoading?: boolean;
    /** Fired when the details popup is opened - use it to lazy-load `groupDetails`. */
    onDetailsOpen?: () => void;
}
export const ProjectInfoCard = (props: IProps) => {
    const {
        project,
        onClick,
        rootStyles,
        createdBy,
        groupDetails,
        groupDetailsLoading,
        onDetailsOpen,
    } = props;

    let rootSx = { ...styles.root };
    if (rootStyles) rootSx = { ...rootSx, ...rootStyles };
    const [detailsOpen, setDetailsOpen] = useState(false);

    const handleDetailsClick = (ev: MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        setDetailsOpen(true);
        onDetailsOpen?.();
    };
    const handleDetailsClose = () => setDetailsOpen(false);

    const assignedCount = project?.assignedStudentsCount ?? 0;
    // Every student has submitted: done, even though it stays listed until its
    // deadline.
    const isAllSubmitted =
        assignedCount > 0 && project?.studentsSubmissionCount === assignedCount;
    const isActive =
        !isProjectDeadlinePassed(project?.assessmentTargetDate) &&
        !isAllSubmitted;

    const resolvedCreatedBy = createdBy || project?.teacherName || '-';
    const fallbackGroupName =
        `${project?.class || ''}${project?.section || ''}`.trim() || '-';
    const groups = groupDetails ?? [];
    const showGroupsLoader = !!groupDetailsLoading && groups.length === 0;
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
    const isIpadOnly = !isMobile && isIpad;

    const formatAssessmentDate = (timestamp: Timestamp | undefined): string => {
        if (!timestamp) return '-';

        const date = new Date(Number(timestamp.seconds) * 1000);

        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }).replace(/ /g, '-');
    };
    return (
        <>
        <Box
            sx={rootSx}
            onClick={onClick}
        >
            <Box sx={{
                display: 'flex',
                gap: { xs: pxToRem(15), md: pxTovW(15), lg: pxTovW(23) },
                boxSizing: 'border-box',
                width: '100%'
            }}>
                <Box sx={styles.imageBox}>
                    <ImageWrapper
                        name="create_project_icon"
                        type="png"
                        parentFolder="icons"
                        styles={{
                            ...styles.image,
                            height: '100%',
                        }}
                        // Uncomment when the project icon is added to the bucket.
                        // path={getMediaBasePath(
                        //     project?.assessmentPosterImgUrl,
                        //     'processedMediaBucket'
                        // )}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        gap: { xs: pxToRem(3), md: pxTovW(4) },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            // alignItems: 'center',
                            justifyContent: 'space-between',
                            height: { xs: pxToRem(17), md: pxTovW(28) },
                            gap: { xs: pxToRem(10), md: pxTovW(16) },
                            marginTop: { xs: pxToRem(2), md: pxTovW(10), lg: pxTovW(5) },
                            marginBottom: { xs: pxToRem(1), md: pxTovW(5) },
                        }}
                    >

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: 'max-content',
                                background: '#475D99',
                                padding: {
                                    xs: `${pxToRem(3)} ${pxToRem(7)} ${pxToRem(4)}`,
                                    md: `${pxTovW(4)} ${pxTovW(14)} ${pxTovW(7)}`,
                                    lg: `${pxTovW(5)} ${pxTovW(14)} ${pxTovW(7)}`,
                                },
                                borderRadius: {
                                    xs: pxToRem(10),
                                    md: pxTovW(20),
                                    lg: pxTovW(15),
                                },
                            }}
                        >
                            <Typography
                                variant={isMobile ? 'smallestText' : 'subText'}
                                color="common.white"
                                fontWeight={700}
                            >
                                Project
                            </Typography>
                        </Box>
                        {isActive ? (<Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: {
                                    xs: pxToRem(5),
                                    md: pxTovW(4),
                                    lg: pxTovW(4),
                                },
                                width: { xs: pxToRem(78), md: pxTovW(140), lg: pxTovW(78) },
                                height: { xs: pxToRem(24), md: pxTovW(34), lg: pxTovW(24) },
                                background: '#FFE3DA',
                                // border: '1px solid #00853F',
                                borderRadius: {
                                    xs: pxToRem(30),
                                    md: pxTovW(50),
                                    lg: pxTovW(50),
                                },
                                // padding: {
                                //     xs: `${pxToRem(12)} ${pxToRem(10)}`,
                                //     md: `${pxTovW(4)} ${pxTovW(10)}`,
                                //     lg: `${pxTovW(4)} ${pxTovW(10)}`,
                                // },
                            }}
                        >
                            <IconWrapper
                                name="project_active"
                                parentFolder="icons"
                                type="png"
                                size="small"
                                customSx={{
                                    height: {
                                        xs: pxToRem(18),
                                        md: pxTovW(22),
                                        lg: pxTovW(18.2),
                                    },
                                    width: {
                                        xs: pxToRem(18),
                                        md: pxTovW(22),
                                        lg: pxTovW(18),
                                    },
                                    // display: "block",
                                    // flexShrink: 0,
                                    // objectFit: "contain",
                                }}
                            />

                            <Typography
                                variant={isMobile ? 'smallestText' : 'subText'}
                                sx={{
                                    color: '#F84307',
                                    fontWeight: 700,
                                    lineHeight: 1,
                                }}
                            >
                                Active
                            </Typography>
                        </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: {
                                        xs: pxToRem(5),
                                        md: pxTovW(4),
                                        lg: pxTovW(4),
                                    },
                                    width: { xs: pxToRem(100), md: pxTovW(140), lg: pxTovW(101) },
                                    height: { xs: pxToRem(24), md: pxTovW(34), lg: pxTovW(24) },
                                    background: '#E0F7EF',
                                    // border: '1px solid #00853F',
                                    borderRadius: {
                                        xs: pxToRem(30),
                                        md: pxTovW(50),
                                        lg: pxTovW(50),
                                    },
                                    // padding: {
                                    //     xs: `${pxToRem(12)} ${pxToRem(10)}`,
                                    //     md: `${pxTovW(4)} ${pxTovW(10)}`,
                                    //     lg: `${pxTovW(4)} ${pxTovW(10)}`,
                                    // },
                                }}
                            >
                                <IconWrapper
                                    name="project_completed"
                                    parentFolder="icons"
                                    type="png"
                                    size="small"
                                    customSx={{
                                        height: {
                                            xs: pxToRem(18),
                                            md: pxTovW(18),
                                            lg: pxTovW(18.2),
                                        },
                                        width: {
                                            xs: pxToRem(18),
                                            md: pxTovW(18),
                                            lg: pxTovW(18),
                                        },
                                        // display: "block",
                                        // flexShrink: 0,
                                        // objectFit: "contain",
                                    }}
                                />

                                <Typography
                                    variant={isMobile ? 'smallestText' : 'subText'}
                                    sx={{
                                        color: '#008A3D',
                                        fontWeight: 700,
                                        lineHeight: 1,
                                    }}
                                >
                                    Completed
                                </Typography>
                            </Box>)}
                    </Box>
                    <Typography
                        variant={isMobile ? 'smallestText' : 'subText'}
                        fontWeight={700}
                        color="primary.main"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordWrap: 'break-word',
                        }}
                    >
                        {project?.class}
                        {project?.section} {project?.subject}
                    </Typography>
                    <Typography
                        variant={'h4'}
                        fontWeight={600}
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordWrap: 'break-word',
                            maxWidth: {
                                xs: pxToRem(190),
                                md: pxTovW(320),
                                lg: pxTovW(220),
                            },
                        }}
                    >
                        {project?.assessmentTitle}
                    </Typography>
                    <Typography
                        variant={isMobile ? 'smallestText' : 'subText'}
                        fontWeight={500}
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordWrap: 'break-word',
                            maxWidth: {
                                xs: pxToRem(190),
                                md: pxTovW(320),
                                lg: pxTovW(220),
                            },
                            // marginTop: { xs: pxToRem(4), md: pxTovW(2) },
                        }}
                    >
                        {project?.moduleName}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: pxToRem(5), md: pxTovW(5) },
                            mt: { xs: pxToRem(5), md: pxTovW(5) },
                        }}
                    >
                        <Typography variant="subText" fontWeight="bold">
                            Details
                        </Typography>
                        <Button
                            onClick={handleDetailsClick}
                            sx={{ padding: 0, minWidth: 'unset' }}
                        >
                            <IconWrapper
                                name="tooltip-black"
                                parentFolder="icons"
                                type="png"
                                size="small"
                                customSx={{
                                    height: {
                                        xs: pxToRem(14),
                                        md: pxTovW(20),
                                        lg: pxTovW(14),
                                    },
                                    width: {
                                        xs: pxToRem(14),
                                        md: pxTovW(20),
                                        lg: pxTovW(14),
                                    },
                                    cursor: 'pointer',
                                }}
                            />
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    width: "100%",
                    py: 1,
                }}
            >
                <Divider
                    sx={{
                        width: "100%",
                        borderColor: "#E0E0E0",
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    boxSizing: 'border-box',
                    justifyContent: 'space-between',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Box sx={styles.iconBox}>
                            <IconWrapper
                                name="project_group"
                                size={isIpadOnly ? 'md' : 'small'}
                                parentFolder="icons"
                                type="png"
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Typography variant="subText" fontWeight={'bold'}>
                                    Group
                                </Typography>
                                <Typography variant="smallestText" fontWeight="regular" color={'#B3B3B3'}>
                                    Type
                                </Typography>
                            </Box>

                        </Box>
                        <Box sx={styles.iconBox}>
                            <IconWrapper
                                name="project_calendar"
                                size={isIpadOnly ? 'md' : 'small'}
                                parentFolder="icons"
                                type="png"
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <Typography
                                    variant="subText" fontWeight={'bold'}
                                >
                                    {formatAssessmentDate(project?.assessmentTargetDate)}
                                </Typography>
                                <Typography variant="smallestText" fontWeight="regular" color={'#B3B3B3'}>
                                    DeadLine
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>

            <Dialog
                open={detailsOpen}
                onClose={handleDetailsClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{ sx: styles.gdPaper }}
            >
                <Box sx={styles.gdHeader}>
                    <Typography sx={styles.gdTitle}>Group Details</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={handleDetailsClose}
                        sx={styles.gdClose}
                    >
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={styles.gdContent}>
                    {showGroupsLoader ? (
                        <Box sx={styles.gdLoader}>
                            <CircularProgress
                                size={22}
                                sx={{ color: GROUP_DETAILS_GREEN }}
                            />
                        </Box>
                    ) : groups.length === 0 ? (
                        <>
                            {/* Group Name */}
                            <Box sx={styles.gdRow}>
                                <GroupsRoundedIcon sx={styles.gdIcon} />
                                <Box>
                                    <Typography sx={styles.gdLabel}>Group Name</Typography>
                                    <Typography sx={styles.gdValue}>
                                        {fallbackGroupName}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={styles.gdDivider} />

                            {/* Created By */}
                            <Box sx={styles.gdRow}>
                                <PersonOutlineRoundedIcon sx={styles.gdPersonIcon} />
                                <Box>
                                    <Typography sx={styles.gdLabel}>Created By</Typography>
                                    <Typography sx={styles.gdValue}>
                                        {resolvedCreatedBy}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={styles.gdDivider} />

                            {/* Members */}
                            <Box sx={styles.gdMembersRow}>
                                <GroupsRoundedIcon sx={styles.gdIcon} />
                                <Box sx={{ width: '100%' }}>
                                    <Typography sx={styles.gdLabel}>Members (0)</Typography>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        groups.map((group, groupIndex) => {
                            const members = group.studentMarks ?? [];
                            return (
                                <Fragment key={group.studentGroupId ? group.studentGroupId.toString() : groupIndex}>
                                    {groupIndex > 0 && (
                                        <Divider sx={styles.gdGroupDivider} />
                                    )}

                                    {/* Group Name */}
                                    <Box sx={styles.gdRow}>
                                        <GroupsRoundedIcon sx={styles.gdIcon} />
                                        <Box>
                                            <Typography sx={styles.gdLabel}>
                                                Group Name
                                            </Typography>
                                            <Typography sx={styles.gdValue}>
                                                {group.groupName || fallbackGroupName}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider sx={styles.gdDivider} />

                                    {/* Created By - the project creator, repeated against each group */}
                                    <Box sx={styles.gdRow}>
                                        <PersonOutlineRoundedIcon
                                            sx={styles.gdPersonIcon}
                                        />
                                        <Box>
                                            <Typography sx={styles.gdLabel}>
                                                Created By
                                            </Typography>
                                            <Typography sx={styles.gdValue}>
                                                {resolvedCreatedBy}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Divider sx={styles.gdDivider} />

                                    {/* Members of this group */}
                                    <Box sx={styles.gdMembersRow}>
                                        <GroupsRoundedIcon sx={styles.gdIcon} />
                                        <Box sx={{ width: '100%' }}>
                                            <Typography sx={styles.gdLabel}>
                                                Members ({members.length})
                                            </Typography>
                                            {members.length > 0 && (
                                                <Box sx={styles.gdMembersList}>
                                                    {members.map((member, index) => {
                                                        const name =
                                                            member.studentName?.trim() ||
                                                            `Student ${member.studentId}`;
                                                        return (
                                                            <Box
                                                                key={`${
                                                                    member.studentId || name
                                                                }-${index}`}
                                                                sx={styles.gdMemberItem}
                                                            >
                                                                <Avatar
                                                                    alt={name}
                                                                    sx={styles.gdMemberAvatar}
                                                                >
                                                                    {getGroupMemberInitials(name)}
                                                                </Avatar>
                                                                <Typography
                                                                    sx={styles.gdMemberName}
                                                                >
                                                                    {name}
                                                                    {member.rollNumber ? (
                                                                        <Box
                                                                            component="span"
                                                                            sx={styles.gdMemberRoll}
                                                                        >
                                                                            {` · Roll ${member.rollNumber}`}
                                                                        </Box>
                                                                    ) : null}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                </Fragment>
                            );
                        })
                    )}
                </Box>
            </Dialog>
        </>
    )
}

export default ProjectInfoCard
